import { useEffect } from 'react';
import { Card, CardContent, Stack, Typography, Chip, Box, CircularProgress, Alert, Button } from '@mui/material';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import IconButton from '@mui/material/IconButton';
import GnssControllerApi from '../gnssControllerApi';
import useGnssStore from '../../../stores/gnssStore';

const gnssController = new GnssControllerApi();

// Fix quality descriptions
const FIX_QUALITY_LABELS = {
  0: { label: 'No Fix', color: 'error', icon: GpsOffIcon },
  1: { label: 'GPS Fix', color: 'success', icon: GpsFixedIcon },
  2: { label: 'DGPS Fix', color: 'success', icon: GpsFixedIcon },
  3: { label: 'PPS Fix', color: 'success', icon: GpsFixedIcon },
  4: { label: 'RTK Fixed', color: 'success', icon: GpsFixedIcon },
  5: { label: 'RTK Float', color: 'warning', icon: GpsNotFixedIcon },
  6: { label: 'Estimated', color: 'warning', icon: GpsNotFixedIcon }
};

export default function GPSStatus() {
  // Subscribe to individual values from Zustand store to prevent infinite loops
  const latitude = useGnssStore((state) => state.latitude);
  const longitude = useGnssStore((state) => state.longitude);
  const altitude = useGnssStore((state) => state.altitude);
  const fixQuality = useGnssStore((state) => state.fixQuality);
  const numSatellites = useGnssStore((state) => state.numSatellites);
  const hdop = useGnssStore((state) => state.hdop);
  const ageSeconds = useGnssStore((state) => state.ageSeconds);
  const loading = useGnssStore((state) => state.loading);
  const error = useGnssStore((state) => state.error);
  const updateGnssData = useGnssStore((state) => state.updateGnssData);
  const setLoading = useGnssStore((state) => state.setLoading);
  const setError = useGnssStore((state) => state.setError);

  // Create gpsData object from individual values
  const gpsData = {
    latitude,
    longitude,
    altitude,
    fix_quality: fixQuality,
    num_satellites: numSatellites,
    hdop,
    age_seconds: ageSeconds
  };

  const fetchGPSData = () => {
    setLoading(true);
    gnssController.gpsData()
      .then(data => {
        if (data.status) {
          updateGnssData(data);
          setError(null); // Clear any previous errors
        } else {
          // Backend returned status=False but HTTP 200 - receiver is working but no fix
          updateGnssData(data); // Update with no-fix data (satellites, fix_quality=0, etc.)
          setError(null); // Don't show error for no-fix condition
        }
      })
      .catch(err => {
        // HTTP error (like 503) - actual communication problem
        setError(err.message || 'Failed to connect to GNSS receiver');
      });
  };

  useEffect(() => {
    fetchGPSData();

    // Auto-refresh every 5 seconds
    const interval = setInterval(fetchGPSData, 5000);

    return () => clearInterval(interval);
  }, []);

  const getFixQualityInfo = () => {
    const fixQuality = gpsData?.fix_quality || 0;
    return FIX_QUALITY_LABELS[fixQuality] || FIX_QUALITY_LABELS[0];
  };

  const formatCoordinate = (value, isLongitude = false) => {
    if (!value) return '-';
    const decimals = 8;
    return value.toFixed(decimals) + '°';
  };

  if (loading && !gpsData.latitude) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading GNSS status...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const fixInfo = getFixQualityInfo();
  const FixIcon = fixInfo.icon;
  const hasValidFix = gpsData.fix_quality > 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SatelliteAltIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              GNSS Receiver Status
            </Typography>
          </Stack>
          <IconButton size="small" onClick={fetchGPSData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {gpsData && (
          <Stack spacing={2}>
            {/* Fix Status */}
            <Stack direction="row" spacing={2} alignItems="center">
              <FixIcon color={fixInfo.color} />
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary">
                  Fix Status
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={fixInfo.label}
                    color={fixInfo.color}
                    size="small"
                  />
                  {gpsData.age_seconds >= 0 && (
                    <Typography variant="caption" color="text.secondary">
                      ({gpsData.age_seconds}s ago)
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Satellites */}
            <Stack direction="row" spacing={2} alignItems="center">
              <SignalCellularAltIcon color={hasValidFix ? 'success' : 'disabled'} />
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary">
                  Satellites Tracked
                </Typography>
                <Typography variant="h6">
                  {gpsData.num_satellites}
                </Typography>
              </Box>
            </Stack>

            {/* HDOP */}
            {hasValidFix && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  HDOP (Horizontal Precision)
                </Typography>
                <Typography variant="body2">
                  {gpsData.hdop.toFixed(2)}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {gpsData.hdop < 2 ? 'Excellent' : gpsData.hdop < 5 ? 'Good' : gpsData.hdop < 10 ? 'Moderate' : 'Poor'}
                  </Typography>
                </Typography>
              </Box>
            )}

            {/* Current Position */}
            {hasValidFix && (
              <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Current Position
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => {
                      const url = `https://www.google.com/maps?q=${gpsData.latitude},${gpsData.longitude}`;
                      window.open(url, '_blank');
                    }}
                    sx={{ minWidth: 'auto', fontSize: '0.7rem', py: 0.25, px: 1 }}
                  >
                    Maps
                  </Button>
                </Stack>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Latitude:</Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {formatCoordinate(gpsData.latitude)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Longitude:</Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {formatCoordinate(gpsData.longitude, true)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Altitude:</Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {gpsData.altitude.toFixed(3)} m
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            )}

            {!hasValidFix && (
              <Alert severity="info" icon={<GpsNotFixedIcon />}>
                Waiting for GNSS fix. Make sure the antenna has a clear view of the sky.
              </Alert>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
